// src/pages/ReceiveDeliveryView.jsx
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Button,
  Stack,
  Collapse,
} from '@mui/material';
import {
  Bus,
  AlertCircle,
  Package,
  User,
  Phone,
  ChevronDown,
  ChevronUp,
  Thermometer,
  DoorOpen,
  RotateCcw,
  Ticket,
  ImageIcon,
  Maximize2,
  FileText,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { ReceiveDeliverySkeleton } from './ReceiveDeliverySkeleton';

function toDateMaybe(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatTime(value) {
  const d = toDateMaybe(value);
  if (!d) return null;
  return d.toLocaleString('fi-FI', { hour: '2-digit', minute: '2-digit' });
}

function formatDay(value) {
  const d = toDateMaybe(value);
  if (!d) return null;
  return d.toLocaleDateString('fi-FI', { weekday: 'long', day: 'numeric', month: 'numeric' });
}

function formatDistance(meters) {
  if (!meters || meters <= 0) return null;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

// "KYS - Kuopion Yliopistollinen Sairaala, Puijonlaaksontie, Kuopio, Finland"
// is far too long for a timeline row. The city is the useful part collapsed;
// the full address lives inside the expanded stop so it is written once.
function shortPlace(info, fallback) {
  if (info?.city) return info.city;
  const s = String(info?.address || fallback || '').trim();
  if (!s) return '—';
  const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length > 1 && /^(finland|suomi)$/i.test(parts[parts.length - 1])) parts.pop();
  return parts[parts.length - 1] || parts[0] || s;
}

const isPdf = (url) => /\.pdf(\?|$)/i.test(url);

const STATUS_CONFIG = {
  active: { label: 'Odottaa kuljettajaa', color: 'warning' },
  ongoing: { label: 'Matkalla', color: 'info' },
  picked: { label: 'Noudettu', color: 'primary' },
  completed: { label: 'Toimitettu', color: 'success' },
  cancelled: { label: 'Peruutettu', color: 'error' },
};

// ─── Lightbox ───
function Lightbox({ url, onClose }) {
  if (!url) return null;
  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(0,0,0,0.92)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1,
        cursor: 'zoom-out',
      }}
    >
      <Box
        component="img"
        src={url}
        alt="Lippu"
        sx={{ maxWidth: '100%', maxHeight: '95vh', objectFit: 'contain', borderRadius: 1 }}
      />
    </Box>
  );
}

// ─── Ticket panel ───
// This is what the driver scanned the QR code for: it sits at the top, always
// open, and shows the ticket large enough to actually read. A 90px thumbnail
// grid was useless for verifying a bus ticket.
function TicketPanel({ tickets, onOpen }) {
  const single = tickets.length === 1;

  const tile = (url, i) => {
    if (isPdf(url)) {
      return (
        <Box
          key={i}
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            flex: single ? '1 1 auto' : '0 0 78%',
            minHeight: 180,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            textDecoration: 'none',
            color: 'text.secondary',
            scrollSnapAlign: 'center',
          }}
        >
          <FileText size={28} />
          <Typography variant="body2" fontWeight={600}>Avaa lippu (PDF)</Typography>
        </Box>
      );
    }

    return (
      <Box
        key={i}
        onClick={() => onOpen(url)}
        sx={{
          position: 'relative',
          flex: single ? '1 1 auto' : '0 0 78%',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          cursor: 'zoom-in',
          scrollSnapAlign: 'center',
        }}
      >
        <Box
          component="img"
          src={url}
          alt={`Lippu ${i + 1}`}
          sx={{
            width: '100%',
            maxHeight: 420,
            objectFit: 'contain',
            display: 'block',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: 'rgba(0,0,0,0.6)',
            color: '#fff',
          }}
        >
          <Maximize2 size={13} />
          <Typography variant="caption">Suurenna</Typography>
        </Box>
        {!single && (
          <Chip
            label={`${i + 1}/${tickets.length}`}
            size="small"
            sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff' }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ px: 2, pt: 2, pb: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25, px: 0.5 }}>
        <Ticket size={18} />
        <Typography variant="subtitle2" fontWeight={700}>
          {tickets.length > 1 ? `Liput (${tickets.length})` : 'Lippu'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Näytä kuljettajalle
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          overflowX: single ? 'visible' : 'auto',
          scrollSnapType: 'x mandatory',
          pb: single ? 0 : 1,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {tickets.map(tile)}
      </Box>
    </Box>
  );
}

// ─── Timeline rail ───
function Rail({ variant = 'stop', last = false }) {
  const dot =
    variant === 'stop'
      ? { size: 12, bgcolor: 'primary.main', border: 'none' }
      : { size: 8, bgcolor: 'background.paper', border: '2px solid', borderColor: 'divider' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
      <Box
        sx={{
          width: dot.size,
          height: dot.size,
          borderRadius: '50%',
          bgcolor: dot.bgcolor,
          border: dot.border,
          borderColor: dot.borderColor,
          mt: 0.75,
        }}
      />
      {!last && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', my: 0.5 }} />}
    </Box>
  );
}

// ─── Endpoint stop (pickup / dropoff) — expands to full address + contact ───
function StopRow({ label, time, place, address, info, contactName, contactPhone, last }) {
  const [open, setOpen] = useState(false);
  const details = info?.details || {};
  const chips = [
    details.apartment && `Asunto ${details.apartment}`,
    details.floor && `Kerros ${details.floor}`,
    details.building,
  ].filter(Boolean);

  const hasMore =
    address !== place || chips.length > 0 || details.instructions || contactName || contactPhone;

  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Rail variant="stop" last={last && !open} />
      <Box sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 2.5 }}>
        <Box
          onClick={() => hasMore && setOpen(!open)}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
            cursor: hasMore ? 'pointer' : 'default',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              {label}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {place}
              </Typography>
              {hasMore && (open ? <ChevronUp size={15} /> : <ChevronDown size={15} />)}
            </Stack>
          </Box>
          {time && (
            <Typography variant="subtitle2" fontWeight={700} sx={{ flexShrink: 0 }}>
              {time}
            </Typography>
          )}
        </Box>

        <Collapse in={open}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">{address}</Typography>
            {chips.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
                {chips.map((c) => (
                  <Chip key={c} label={c} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.75rem' }} />
                ))}
              </Stack>
            )}
            {details.instructions && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                {details.instructions}
              </Typography>
            )}
            {(contactName || contactPhone) && (
              <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 1.5 }}>
                {contactName && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <User size={14} />
                    <Typography variant="body2">{contactName}</Typography>
                  </Stack>
                )}
                {contactPhone && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    component="a"
                    href={`tel:${contactPhone.replace(/\s/g, '')}`}
                    sx={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <Phone size={14} />
                    <Typography variant="body2">{contactPhone}</Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

// ─── Intermediate leg row ───
// Car legs are the courier driving between the two endpoints already shown
// above and below, so they get a muted line with no addresses. Bus and wait
// legs carry information nothing else on the page does.
function LegRow({ step }) {
  const from = formatTime(step.start);
  const to = formatTime(step.end);
  const range = from && to ? `${from} – ${to}` : from || '';

  if (step.type === 'car') {
    return (
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Rail variant="minor" />
        <Box sx={{ flex: 1, minWidth: 0, pb: 2.5 }}>
          <Typography variant="caption" color="text.disabled">
            Kuriiri autolla{range ? ` · ${range}` : ''}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (step.type === 'wait') {
    return (
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Rail variant="minor" />
        <Box sx={{ flex: 1, minWidth: 0, pb: 2.5 }}>
          <Typography variant="caption" color="text.secondary">
            Odotus {shortPlace(null, step.origin)}{range ? ` · ${range}` : ''}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Rail variant="minor" />
      <Box sx={{ flex: 1, minWidth: 0, pb: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Bus size={14} />
              {step.shortName && (
                <Chip
                  label={step.shortName}
                  size="small"
                  color="primary"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              )}
              {(step.exactName || step.busCompany) && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {step.exactName || step.busCompany}
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {step.origin} → {step.destination}
            </Typography>
          </Box>
          {range && (
            <Typography variant="caption" fontWeight={600} sx={{ flexShrink: 0 }}>
              {range}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

// ─── Collapsible Section (non-ticket attachments) ───
function Section({ icon: Icon, title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 1.5,
          px: 1,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setOpen(!open)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {Icon && <Icon size={18} style={{ flexShrink: 0 }} />}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
            {subtitle && !open && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ pl: 4, pr: 1, pt: 0.5, pb: 2 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

// ─── Small thumbnail grid (used for non-ticket attachments only) ───
function ImageGallery({ images, onOpen }) {
  if (!images?.length) return null;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 1 }}>
      {images.map((url, i) =>
        isPdf(url) ? (
          <Box
            key={i}
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              aspectRatio: '1',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              textDecoration: 'none',
              color: 'text.secondary',
            }}
          >
            <ImageIcon size={20} />
            <Typography variant="caption">PDF</Typography>
          </Box>
        ) : (
          <Box
            key={i}
            onClick={() => onOpen(url)}
            sx={{
              aspectRatio: '1',
              borderRadius: 1.5,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              cursor: 'zoom-in',
              '&:hover': { opacity: 0.85 },
              transition: 'opacity 0.15s',
            }}
          >
            <Box
              component="img"
              src={url}
              alt={`Liite ${i + 1}`}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        )
      )}
    </Box>
  );
}

// ─── Main Component ───
export function ReceiveDeliveryView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const deliveryId = searchParams.get('d');

  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!deliveryId) {
      setError('Lähetystunnus puuttuu (d).');
      setLoading(false);
      return;
    }

    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        const fn = httpsCallable(functions, 'getTrackingInfo');
        const res = await fn({ deliveryId });

        if (!res?.data?.delivery) {
          setError('Lähetystä ei löytynyt tai sinulla ei ole oikeutta nähdä sitä.');
          return;
        }

        setDelivery(res.data.delivery);
      } catch (e) {
        console.error('getTrackingInfo error', e);
        setError('Virhe ladattaessa lähetystä.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [deliveryId]);

  if (loading) return <ReceiveDeliverySkeleton />;

  if (error || !delivery) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error" icon={<AlertCircle size={20} />} sx={{ mb: 2 }}>
          {error || 'Lähetystä ei löytynyt.'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          Palaa etusivulle
        </Button>
      </Container>
    );
  }

  const status = delivery.status || 'active';
  const statusConfig = STATUS_CONFIG[status] || { label: status, color: 'default' };

  const pickupTs = delivery.pickUpTime || delivery.pickupTime;
  const targetTs = delivery.targetTime;

  const fromAddress = delivery.fromInfo?.address || delivery.origin || '—';
  const toAddress = delivery.toInfo?.address || delivery.destination || '—';
  const fromCity = shortPlace(delivery.fromInfo, delivery.origin);
  const toCity = shortPlace(delivery.toInfo, delivery.destination);

  const bus = delivery.busTransportData || null;
  const steps = Array.isArray(bus?.legs) ? bus.legs : [];

  const meta = [formatDay(pickupTs), formatDistance(delivery.distance), formatDuration(delivery.duration)]
    .filter(Boolean)
    .join(' · ');

  const ticketImages = Array.isArray(delivery.ticketImages) ? delivery.ticketImages : [];
  const otherImages = Array.isArray(delivery.images) ? delivery.images : [];
  const isBus = !!bus;

  const extras = [
    delivery.returnParcels && { icon: <RotateCcw size={14} />, label: 'Palautuspaketti' },
    delivery.temperatureControl && { icon: <Thermometer size={14} />, label: 'Lämpötilahallinta' },
    delivery.canBeLeftAtDoor && { icon: <DoorOpen size={14} />, label: 'Voi jättää ovelle' },
  ].filter(Boolean);

  const packageLine = [delivery.packageSize, delivery.weightKg ? `${delivery.weightKg} kg` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <Container maxWidth="sm" sx={{ py: 4, pt: 12 }}>
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>

        {/* ── Header ── */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
          <Chip label={statusConfig.label} color={statusConfig.color} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
            {delivery.deliveryId || delivery.id || deliveryId}
            {meta ? ` · ${meta}` : ''}
          </Typography>
        </Box>

        <Divider />

        {/* ── Ticket — first thing on screen, this is why the QR was scanned ── */}
        {ticketImages.length > 0 ? (
          <>
            <TicketPanel tickets={ticketImages} onOpen={setLightbox} />
            <Divider />
          </>
        ) : isBus ? (
          <>
            <Box sx={{ px: 3, py: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Ticket size={16} />
                <Typography variant="body2" color="text.secondary">
                  Lippua ei ole vielä lisätty — tarkista sivu myöhemmin uudelleen.
                </Typography>
              </Stack>
            </Box>
            <Divider />
          </>
        ) : null}

        {/* ── Journey ── */}
        {/* Pickup and dropoff are the first and last stops of one timeline,
            so neither the cities nor the times are printed twice. */}
        <Box sx={{ px: 3, py: 3 }}>
          <StopRow
            label="Nouto"
            time={formatTime(pickupTs)}
            place={fromCity}
            address={fromAddress}
            info={delivery.fromInfo}
            contactName={delivery.senderInfo?.name}
            contactPhone={delivery.senderPhone}
          />

          {steps.map((step, i) => <LegRow key={i} step={step} />)}

          <StopRow
            label="Toimitus"
            time={formatTime(targetTs)}
            place={toCity}
            address={toAddress}
            info={delivery.toInfo}
            contactName={delivery.receiverInfo?.name}
            contactPhone={delivery.receiverPhone}
            last
          />
        </Box>

        {/* ── Other attachments ── */}
        {otherImages.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 0.5 }}>
              <Section
                icon={ImageIcon}
                title="Liitteet"
                subtitle={`${otherImages.length} kuva${otherImages.length !== 1 ? 'a' : ''}`}
              >
                <ImageGallery images={otherImages} onOpen={setLightbox} />
              </Section>
            </Box>
          </>
        )}

        {/* ── Package + options, one row ── */}
        {(packageLine || extras.length > 0) && (
          <>
            <Divider />
            <Box sx={{ px: 3, py: 2 }}>
              <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
                {packageLine && (
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mr: 1 }}>
                    <Package size={15} />
                    <Typography variant="body2" color="text.secondary">{packageLine}</Typography>
                  </Stack>
                )}
                {extras.map((x) => (
                  <Chip key={x.label} icon={x.icon} label={x.label} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          </>
        )}

        {/* ── Footer ── */}
        <Divider />
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Button variant="outlined" size="small" onClick={() => navigate('/')} sx={{ borderRadius: 2 }}>
            Palaa etusivulle
          </Button>
        </Box>
      </Paper>

      <Lightbox url={lightbox} onClose={() => setLightbox(null)} />
    </Container>
  );
}